// Adaptadores para reusar GrupoFormDialog (pensado para Wialon/Leasecontrol)
// con los grupos ("cuadrillas") de TRS Rastreo — mismo editor completo
// (nombre + usuarios + unidades), pero:
//  1) el shape de un grupo TRS es distinto (cua_descripcion, cuadrilla_relacion,
//     usuarios_vinculados en vez de nm/unidades_detalle/usuarios_detalle), asi
//     que se normaliza antes de pasarlo como prop `grupo`.
//  2) la API de rastreo_bo edita por DELTA (usuarios_agregar/remover,
//     vehiculos_agregar/remover) en vez de "mandar la lista final completa"
//     como Wialon/Leasecontrol — el adaptador calcula el diff contra el grupo
//     original antes de pegarle a editarGrupo.
//  3) listarUsuariosSimple/listarVehiculosSimple de TRS no paginan ni buscan
//     server-side (traen TODO) — se pagina/filtra en el propio frontend para
//     poder reusar el mismo CheckTable paginado de GrupoFormDialog.
import { listarUsuariosSimple, listarVehiculosSimple, crearGrupo, editarGrupo } from "../services/trsRastreoService";

export const normalizeTrsGrupoParaDialog = (grupo) => {
  if (!grupo) return null;
  const unidadesActivas = (grupo.cuadrilla_relacion || []).filter((r) => r.cuarel_activo);
  return {
    id: grupo.id,
    nm: grupo.cua_descripcion,
    name: grupo.cua_descripcion,
    usuarios_detalle: (grupo.usuarios_vinculados || []).map((u) => ({
      id: u.usuario_id,
      nm: u.usu_nombre || u.usu_username,
      name: u.usu_nombre || u.usu_username,
    })),
    unidades: unidadesActivas.map((r) => String(r.for_id)),
    unidades_detalle: unidadesActivas.map((r) => ({
      id: r.for_id,
      nm: r.info_relacionado?.veh_alias || r.info_relacionado?.veh_placa,
      uid: [r.info_relacionado?.veh_placa, r.info_relacionado?.veh_modid].filter(Boolean).join(" · ") || "-",
    })),
  };
};

const paginarYFiltrar = (rows, { search = "", page = 1, limit = 50 } = {}) => {
  const s = String(search || "").trim().toLowerCase();
  const filtrados = s
    ? rows.filter((r) => [r.nm, r.uid, r.external_username].some((v) => String(v || "").toLowerCase().includes(s)))
    : rows;
  const totalPages = Math.max(Math.ceil(filtrados.length / limit), 1);
  const start = (page - 1) * limit;
  return { data: { data: filtrados.slice(start, start + limit), page, totalPages } };
};

// listUsers/listUnits para GrupoFormDialog — mismo shape {data:{data,page,totalPages}}
// que ya esperan sus CheckTable, armado en el cliente ya que TRS no pagina esto.
export const trsListUsuariosParaGrupo = async (params = {}) => {
  const resp = await listarUsuariosSimple();
  const rows = (resp?.data?.data || []).map((u) => ({
    id: u.id,
    nm: u.usu_nombre || u.usu_username,
    external_username: u.usu_username,
  }));
  return paginarYFiltrar(rows, params);
};

export const trsListVehiculosParaGrupo = async (params = {}) => {
  const resp = await listarVehiculosSimple();
  const rows = (resp?.data?.data || []).map((v) => ({
    id: v.id,
    nm: v.veh_alias || v.veh_placa,
    // La columna "Dato" del CheckTable mostraba solo el modem/IMEI — cuando
    // el alias no coincidia con la placa, la placa quedaba invisible en el
    // picker. Se combinan placa + modem para que siempre se vea la placa.
    uid: [v.veh_placa, v.veh_modid].filter(Boolean).join(" · ") || "-",
  }));
  return paginarYFiltrar(rows, params);
};

// createGroup para GrupoFormDialog mode="create".
export const trsCrearGrupoParaDialog = ({ nombre, userIds = [], units = [] }) =>
  crearGrupo({
    cua_descripcion: nombre,
    cua_activo: true,
    usuarios_web: userIds.map(Number).filter((n) => Number.isFinite(n)),
    vehiculos: units.map(Number).filter((n) => Number.isFinite(n)),
  });

// updateGroup para GrupoFormDialog mode="edit" — necesita el grupo ORIGINAL
// (sin normalizar) para poder calcular el delta agregar/remover; se arma como
// closure en el componente que abre el dialogo, capturando el grupo actual.
export const trsEditarGrupoParaDialog = (grupoOriginal) => (itemId, { nombre, userIds = [], units = [] }) => {
  const usuarioIdsOriginales = (grupoOriginal?.usuarios_vinculados || []).map((u) => String(u.usuario_id));
  const vehiculoIdsOriginales = (grupoOriginal?.cuadrilla_relacion || [])
    .filter((r) => r.cuarel_activo)
    .map((r) => String(r.for_id));

  const usuariosFinal = new Set(userIds.map(String));
  const vehiculosFinal = new Set(units.map(String));
  const usuariosOriginales = new Set(usuarioIdsOriginales);
  const vehiculosOriginales = new Set(vehiculoIdsOriginales);

  const nombreCambio = String(nombre || "").trim();
  const payload = {
    usuarios_agregar: [...usuariosFinal].filter((id) => !usuariosOriginales.has(id)).map(Number),
    usuarios_remover: usuarioIdsOriginales.filter((id) => !usuariosFinal.has(id)).map(Number),
    vehiculos_agregar: [...vehiculosFinal].filter((id) => !vehiculosOriginales.has(id)).map(Number),
    vehiculos_remover: vehiculoIdsOriginales.filter((id) => !vehiculosFinal.has(id)).map(Number),
  };
  if (nombreCambio && nombreCambio !== (grupoOriginal?.cua_descripcion || "")) {
    payload.cua_descripcion = nombreCambio;
  }

  return editarGrupo(itemId, payload);
};
